import { useState, useEffect } from "react";
import { toast } from "sonner";
import { DashboardShell } from "@/components/layout/DashboardShell";
import { carrierNav } from "@/pages/carrier/nav";
import { StatusPill } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Profile() {
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e.target.value });

  useEffect(() => { api.get("/carrier/profile").then((r) => setP(r.data)); }, []);
  if (!p) return <DashboardShell nav={carrierNav}><div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-xd-orange" size={32} /></div></DashboardShell>;

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/carrier/onboarding", { step: "Profile", data: {
        legal_name: p.legal_name, dba: p.dba, usdot: p.usdot, mc: p.mc, ein: p.ein,
        business_address: p.business_address, phone: p.phone, home_market: p.home_market,
        insurance_provider: p.insurance_provider, insurance_expiration: p.insurance_expiration,
        authority_status: p.authority_status, factoring_company: p.factoring_company } });
      toast.success("Profile saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  return (
    <DashboardShell nav={carrierNav} title="My Business Profile">
      <div className="mb-6 flex items-center gap-3">
        {p.verified ? (
          <span className="inline-flex items-center gap-2 rounded-full border border-xd-green/40 bg-xd-green/10 text-xd-green px-4 py-1.5 text-sm font-bold"><ShieldCheck size={16} /> XD VERIFIED CARRIER</span>
        ) : (
          <StatusPill status={p.verification_status} />
        )}
      </div>

      <div className="max-w-3xl bg-xd-paper border border-xd-border rounded-xl p-6 grid gap-4 sm:grid-cols-2">
        <F label="Legal name" testid="pf-legal" value={p.legal_name} onChange={set("legal_name")} />
        <F label="DBA" testid="pf-dba" value={p.dba} onChange={set("dba")} />
        <F label="USDOT" testid="pf-usdot" value={p.usdot} onChange={set("usdot")} />
        <F label="MC" testid="pf-mc" value={p.mc} onChange={set("mc")} />
        <F label="EIN" testid="pf-ein" value={p.ein} onChange={set("ein")} />
        <F label="Authority status" testid="pf-authority" value={p.authority_status} onChange={set("authority_status")} />
        <F label="Phone" testid="pf-phone" value={p.phone} onChange={set("phone")} />
        <F label="Home market" testid="pf-home" value={p.home_market} onChange={set("home_market")} />
        <F label="Insurance provider" testid="pf-ins" value={p.insurance_provider} onChange={set("insurance_provider")} />
        <F label="Insurance expiration" type="date" testid="pf-ins-exp" value={p.insurance_expiration} onChange={set("insurance_expiration")} />
        <F label="Factoring company" testid="pf-factoring" value={p.factoring_company} onChange={set("factoring_company")} />
        <div className="sm:col-span-2"><Label className="text-xd-muted text-sm">Business address</Label><Input data-testid="pf-address" value={p.business_address || ""} onChange={set("business_address")} className="mt-1.5 bg-xd-surface border-xd-border h-11" /></div>
        <div className="sm:col-span-2"><Button data-testid="pf-save" onClick={save} disabled={saving} className="bg-xd-orange hover:bg-xd-orangeH text-white">{saving ? <Loader2 className="animate-spin" size={16} /> : "Save Profile"}</Button></div>
      </div>
    </DashboardShell>
  );
}

function F({ label, testid, ...props }) {
  return <div><Label className="text-xd-muted text-sm">{label}</Label><Input data-testid={testid} {...props} value={props.value || ""} className="mt-1.5 bg-xd-surface border-xd-border h-11" /></div>;
}
